import { useCallback } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import {
  utilsAtom,
  stageRefAtom,
  layerRefAtom,
  posScaleAtom,
  boardAtom,
  shouldFitWhenPlacingElementRefAtom,
  showTemplateCreationLoaderModalAtom,
  syncServiceAtom,
} from "state-atoms";
import { getTemplateData } from "frontend/api";
import {
  centerElementsAroundOrigin,
  createNewFileMetadata,
  measureTemplate,
  separateRawDataToElementsAndMetadata,
} from "frontend/utils/template-utils";
import { CanvasTemplate } from "shared/datamodel/schemas/canvas-template";
import {
  findNonCollidingPositionWithRects,
  getExistingElementRects,
  hasAnyCollision,
  Rect,
} from "frontend/utils/find-non-colliding-position";
import {
  animateCenterToElements,
  centerToElements,
  waitForElements,
} from "frontend/canvas-controls/zoom-controls/fit-to-stage";
import { clamp, viewportToStage } from "utils/math-utils";
import consts from "shared/consts";
import BoundingBox from "frontend/geometry/bounding-box";
import { Board, Flags, Point } from "shared/datamodel/schemas";
import tracking from "frontend/tracking";
import { useBoardValue } from "frontend/state/board-state";
import { useBoardTasks } from "./use-board-integrations";
import { on } from "utils/fn-utils";

function useCreateTemplateIntegrationIfNeeded(board?: Board | null) {
  const { mondayIntegration, createMondayBoardForTemplate } = useBoardTasks(board?.documentId);
  const setShowTemplateCreationLoaderModal = useSetAtom(showTemplateCreationLoaderModalAtom);
  return useCallback(
    async (
      templateMetadata: CanvasTemplate["metadata"],
      elements: any,
      elementsMetadata: any,
      onStatusChange?: (status: string | null) => void
    ) => {
      const { mondaySolutionId, mondayColumns } = templateMetadata || {};

      if (!mondaySolutionId || !mondayIntegration || !board?.documentId) {
        return { elements, metadata: elementsMetadata };
      }

      onStatusChange?.(null);

      setShowTemplateCreationLoaderModal(true);

      const { elements: mutatedElements = {}, metadata: mutatedMetadata = {} } =
        (await createMondayBoardForTemplate(mondaySolutionId, mondayColumns, elements, elementsMetadata)) ?? {};

      setShowTemplateCreationLoaderModal(false);

      return { elements: mutatedElements, metadata: mutatedMetadata };
    },
    [mondayIntegration, createMondayBoardForTemplate, board?.documentId, setShowTemplateCreationLoaderModal]
  );
}

export function usePlaceTemplate() {
  const [{ board }] = useBoardValue();
  const syncService = useAtomValue(syncServiceAtom);
  const createTemplateIntegrationIfNeeded = useCreateTemplateIntegrationIfNeeded(board);
  const canvasUtils = useAtomValue(utilsAtom(board?.documentId));

  return useCallback(
    async (
      template: Partial<CanvasTemplate>,
      getTemplateData: (
        templateId: string
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ) => Promise<{ data: any; metadata: CanvasTemplate["metadata"] }>,
      position: Point,
      source: string,
      onStatusChange?: (status: string | null) => void
    ) => {
      let elements, metadata, otherMetadata;

      if (!board) {
        return null;
      }

      if (template.data) {
        elements = template.data.elements;
        metadata = template.data.metadata;
        otherMetadata = template.metadata;
      } else {
        if (!template.id) {
          return null;
        }
        const templateData = await getTemplateData(template.id);
        otherMetadata = templateData.metadata;
        const separated = separateRawDataToElementsAndMetadata(templateData.data);
        elements = separated.elements;
        metadata = separated.metadata;
      }

      const mutated = await createTemplateIntegrationIfNeeded(otherMetadata, elements, metadata, onStatusChange);
      elements = mutated.elements;
      metadata = mutated.metadata;

      const newElements = new Map(Object.entries(centerElementsAroundOrigin(elements)));
      if (metadata) {
        metadata = await createNewFileMetadata(metadata, board.documentId, board.accountId, "File");
        await syncService?.mutate.mergeMetadata(Object.entries(metadata));
      }

      // Elements that are created from template are flagged as system to enable without restriction
      // eslint-disable-next-line unicorn/no-array-for-each
      newElements.forEach((element) => {
        element.flags = on(element.flags, Flags.CreatedBySystem);
      });

      tracking.trackAnalyticsEvent("new_template_placed", {
        category: consts.TRACKING_CATEGORY.CANVAS_ACTION,
        stringValue: template.id,
        property: board.accountId,
        context: template.name,
        alias: template.alias,
        templateId: template.id,
        templateName: template.name,
        source,
      });

      onStatusChange?.(null);

      return canvasUtils?.copyElements(newElements, position, true) ?? null;
    },
    [board, canvasUtils, createTemplateIntegrationIfNeeded, syncService?.mutate]
  );
}

/**
 * Hook for auto-placing templates on the canvas
 * Measures template size, finds non-colliding position, and places template automatically
 */
export function useTemplateAutoPlacement() {
  const stageRef = useAtomValue(stageRefAtom);
  const layerRef = useAtomValue(layerRefAtom);
  const board = useAtomValue(boardAtom);
  const posScale = useAtomValue(posScaleAtom);
  const setPosScale = useSetAtom(posScaleAtom);
  const shouldFitWhenPlacingElementRef = useAtomValue(shouldFitWhenPlacingElementRefAtom);
  const canvasUtils = useAtomValue(utilsAtom(board?.documentId));
  const placeTemplate = usePlaceTemplate();

  return useCallback(
    async (
      template: Pick<CanvasTemplate, "id"> & Partial<CanvasTemplate>,
      source: string,
      onStatusChange?: (status: string | null) => void
    ): Promise<void> => {
      if (!board || !stageRef?.current || !layerRef?.current) {
        return;
      }

      try {
        onStatusChange?.("Loading template...");

        let templateDataForMeasurement;
        let templateWithData = template;

        if (template.data) {
          templateDataForMeasurement = template.data;
        } else {
          const templateData = await getTemplateData(template.id);
          const separated = separateRawDataToElementsAndMetadata(templateData.data);
          templateDataForMeasurement = templateData.data;
          templateWithData = {
            ...template,
            data: { elements: separated.elements, metadata: separated.metadata },
            metadata: templateData.metadata,
          };
        }

        // Measure template size
        const measurement = measureTemplate(templateDataForMeasurement);

        if (!measurement) {
          // Fallback if measurement fails - use center of viewport
          console.warn("Template measurement failed, using viewport center");
        }

        // Get current viewport center and zoom
        // Viewport center in screen space is (viewportWidth/2, viewportHeight/2)
        // Convert to stage coordinates using viewportToStage
        const viewportWidth = stageRef.current?.width() || window.innerWidth;
        const viewportHeight = stageRef.current?.height() || window.innerHeight;
        const currentViewportCenter = viewportToStage(posScale, {
          x: viewportWidth / 2,
          y: viewportHeight / 2,
          width: 0,
          height: 0,
        });

        const currentZoom = posScale.scale || 1;

        let position: Point;
        let needsZoomAdjustment = false;
        let targetZoom = currentZoom;
        let placedAtViewportCenter = false;

        if (measurement) {
          const templateRect: Rect = measurement.boundingBox.asRect();
          const templateWidth = templateRect.width;
          const templateHeight = templateRect.height;

          // Calculate template position at viewport center (center preference)
          const templateCenterX = currentViewportCenter.x;
          const templateCenterY = currentViewportCenter.y;
          const templateTopLeftX = templateCenterX - templateWidth / 2;
          const templateTopLeftY = templateCenterY - templateHeight / 2;
          const candidateRect: Rect = {
            x: templateTopLeftX,
            y: templateTopLeftY,
            width: templateWidth,
            height: templateHeight,
          };

          // Get existing element rectangles for collision checking
          const existingRects = getExistingElementRects(layerRef);

          // Check if template fits in viewport at current zoom
          const scaledTemplateWidth = templateWidth * currentZoom;
          const scaledTemplateHeight = templateHeight * currentZoom;
          const fitsInViewport = scaledTemplateWidth <= viewportWidth && scaledTemplateHeight <= viewportHeight;

          // Check for collision at viewport center
          const hasCollision = existingRects.length > 0 && hasAnyCollision(candidateRect, existingRects);

          if (hasCollision) {
            // Collision at viewport center - find first non-colliding position
            // Then find suitable zoom at that position if needed
            position = findNonCollidingPositionWithRects(templateRect, existingRects, currentViewportCenter);
            placedAtViewportCenter = false;

            // If template doesn't fit in viewport at current zoom, calculate appropriate zoom
            if (!fitsInViewport) {
              // Calculate appropriate zoom to fit template in viewport
              const scaleX = viewportWidth / templateWidth;
              const scaleY = viewportHeight / templateHeight;
              targetZoom = clamp(Math.min(scaleX, scaleY) * 0.9, consts.STAGE_ZOOM.MIN, 1);
              needsZoomAdjustment = true;
            }
          } else {
            // No collision at viewport center - use it (center preference)
            position = currentViewportCenter;
            placedAtViewportCenter = true;

            // If template doesn't fit in viewport at current zoom, calculate appropriate zoom
            if (!fitsInViewport) {
              // Calculate appropriate zoom to fit template in viewport
              const scaleX = viewportWidth / templateWidth;
              const scaleY = viewportHeight / templateHeight;
              targetZoom = clamp(Math.min(scaleX, scaleY) * 0.9, consts.STAGE_ZOOM.MIN, 1); // 0.9 for padding
              needsZoomAdjustment = true;
            }
          }
        } else {
          position = currentViewportCenter;
          placedAtViewportCenter = true;
        }

        onStatusChange?.("Placing template...");

        if (shouldFitWhenPlacingElementRef !== null) {
          shouldFitWhenPlacingElementRef.current = true;
        }

        const newElementIds = await placeTemplate(templateWithData, getTemplateData, position, source, onStatusChange);

        if (shouldFitWhenPlacingElementRef !== null) {
          shouldFitWhenPlacingElementRef.current = false;
        }

        if (newElementIds && newElementIds.length > 0) {
          const newNodes = await waitForElements(layerRef.current, stageRef.current, newElementIds);

          if (newNodes.length > 0 && stageRef) {
            if (placedAtViewportCenter && !needsZoomAdjustment) {
              // Placed at viewport center, no zoom adjustment needed - keep viewport as is
              // Use animateCenterToElements with maintainCurrentScale to center without changing zoom
              animateCenterToElements(
                stageRef,
                newNodes,
                (posScale) => {
                  setPosScale(posScale);
                },
                0.15,
                { maintainCurrentScale: true, animationDuration: 200 }
              );
            } else if (placedAtViewportCenter && needsZoomAdjustment) {
              // Placed at viewport center but zoom adjusted - adjust position to keep template centered
              // Calculate new position to keep the template centered in viewport
              const templateBbox = BoundingBox.expandOnAll(
                newNodes.map((node) => {
                  return node.getClientRect({
                    relativeTo: stageRef.current as any,
                    skipShadow: true,
                  });
                })
              );

              if (BoundingBox.isValid(templateBbox)) {
                const centerX = viewportWidth / 2;
                const centerY = viewportHeight / 2;
                const templateCenterX = templateBbox.x + templateBbox.width / 2;
                const templateCenterY = templateBbox.y + templateBbox.height / 2;

                const newX = centerX - templateCenterX * targetZoom;
                const newY = centerY - templateCenterY * targetZoom;

                setPosScale({ x: newX, y: newY, scale: targetZoom });
              }
            } else {
              // Placed at non-colliding position - center on that position
              if (needsZoomAdjustment) {
                // Center with zoom adjustment
                const targetPosScale = centerToElements(stageRef, newNodes, 0.15, {
                  maintainCurrentScale: false,
                });

                if (targetPosScale) {
                  setPosScale(targetPosScale);
                }
              } else {
                // Center without zoom adjustment - use animation
                animateCenterToElements(
                  stageRef,
                  newNodes,
                  (posScale) => {
                    setPosScale(posScale);
                  },
                  0.15,
                  { maintainCurrentScale: true, animationDuration: 200 }
                );
              }
            }

            // Always select elements and enable transformer at the end
            canvasUtils?.setSelectedElementIds(newElementIds);
            canvasUtils?.enableTransformer();
          }
        }

        onStatusChange?.(null);
      } catch (error) {
        console.error("Error auto-placing template:", error);
        onStatusChange?.(null);
      }
    },
    [board, stageRef, layerRef, posScale, setPosScale, canvasUtils, shouldFitWhenPlacingElementRef, placeTemplate]
  );
}
